import './index.scss'

import { useMemo, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'

import ErrorView from '@components/ErrorView'
import LoadingIndicator from '@components/LoadingIndicator'
import PageHeader from '@components/PageHeader'
import useVolunteerTeams from '@hooks/Volunteering/useVolunteerTeams'
import useVolunteerTrainingAssignments from '@hooks/Volunteering/useVolunteerTrainingAssignments'
import { TrainingVolunteerAssignmentBundle } from '@interfaces/training.interface'
import { RoutedPerson, personPagePath } from '@pages/v2/People/PersonPage'
import AssignTrainingModal from './AssignTrainingModal'

import bookIcon from 'url:@assets/icons/book.svg';
import infoIcon from 'url:@assets/icons/infocircle.svg';
import plusIcon from 'url:@assets/icons/plusNoCircle.svg';

// What the teams page hands over so the header reads correctly before the
// teams request comes back.
type RoutedVolunteer = {
  personName?: string;
  teamName?: string;
};

export default function VolunteerPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('volunteer');
  const pathParams = useParams();
  const teamID = pathParams.teamID;
  const personID = pathParams.personID;
  const routedVolunteer = (location.state as RoutedVolunteer | null) ?? null;

  const { assignments, loading, hasLoaded, error, refetch, addAssignment } = useVolunteerTrainingAssignments(personID);

  // Teams carry the names this page shows: the volunteer's own, the team they
  // were opened from, and any other team an assignment came from.
  const { teams } = useVolunteerTeams();
  const teamBundle = teams?.results.find((bundle) => bundle.team.id === teamID) ?? null;
  const member = teamBundle?.members.find((teamMember) => teamMember.personID === personID) ?? null;

  const personName = member?.attributes.personName || routedVolunteer?.personName || t('fallbackName');
  const teamName = teamBundle?.team.attributes.name || routedVolunteer?.teamName;

  const teamNameByID = useMemo(() => {
    return new Map((teams?.results ?? []).map((bundle) => [bundle.team.id, bundle.team.attributes.name]));
  }, [teams]);

  const sortedAssignments = useMemo(() => sortAssignments(assignments), [assignments]);

  // Keyed so each open starts back at the Module picker with fresh defaults.
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignModalKey, setAssignModalKey] = useState(0);

  const openAssignModal = () => {
    setAssignModalKey((currentKey) => currentKey + 1);
    setIsAssignModalOpen(true);
  };

  if (!personID) {
    return <Navigate to='/volunteering' replace />
  }

  return <div className='volunteer-page'>
    <PageHeader
      title={personName}
      subtitle={teamName}
      backLocation='/volunteering'
      links={[
        {
          icon: infoIcon,
          title: t('details'),
          onClick: () => {
            const state: RoutedPerson = { personName, backLocation: location.pathname };
            navigate(personPagePath(personID), { state });
          }
        }
      ]} />

    <AssignTrainingModal
      key={`assign-training-${assignModalKey}`}
      isOpen={isAssignModalOpen}
      teamID={teamID}
      personID={personID}
      personName={personName}
      assignedModuleIDs={assignments.map((bundle) => bundle.assignment.moduleID)}
      close={() => setIsAssignModalOpen(false)}
      onAssigned={(bundle) => {
        setIsAssignModalOpen(false);

        // The new row lands right away; the refetch behind it reconciles with
        // whatever the server actually stored.
        if (bundle) {
          addAssignment(bundle);
        }

        void refetch();
      }}
    />

    <section className='volunteer-assignments'>
      {loading && !hasLoaded ? <div className='training-state'>
        <LoadingIndicator fullScreen={false} />
      </div> : <>
        {error ? <ErrorView
          error={error}
          buttonText={t('retry')}
          buttonAction={() => refetch()}
          fullHeight /> : <>
          {sortedAssignments.length > 0 ? <div className='assignments-header'>
            <h2>{t('assignedHeading')}</h2>

            <button type='button' className='assign-training-button' onClick={openAssignModal}>
              <img src={plusIcon} alt='' aria-hidden='true' />
              {t('assignTraining')}
            </button>
          </div> : null}

          {sortedAssignments.length === 0 ? <ErrorView
            icon={bookIcon}
            title={t('emptyTitle')}
            error={t('emptyMessage', { person: personName })}
            buttonText={t('assignTraining')}
            buttonAction={openAssignModal}
            fullHeight /> : <ol className='assignment-list'>
            {sortedAssignments.map((bundle) => {
              const assignment = bundle.assignment;
              // Assignments from elsewhere are labelled; the team this page was
              // opened from is already in the header.
              const otherTeamName = assignment.volunteerTeamID === teamID ?
                null :
                teamNameByID.get(assignment.volunteerTeamID) ?? t('anotherTeam');

              return <AssignmentRow
                key={assignment.moduleID}
                bundle={bundle}
                otherTeamName={otherTeamName}
              />
            })}
          </ol>}
        </>}
      </>}
    </section>
  </div>
}

function AssignmentRow(props: {
  bundle: TrainingVolunteerAssignmentBundle;
  otherTeamName: string | null;
}) {
  const { t } = useTranslation('volunteer');
  const assignment = props.bundle.assignment;
  const module = props.bundle.module ?? null;
  const attributes = module?.attributes ?? null;

  const completedCount = assignment.completedResourceIDs.length;
  const dueDate = parseDate(assignment.dueDate);
  const isOverdue = dueDate !== null && dueDate.getTime() < Date.now();
  
  const isCompleted = assignment.completionDate != null;

  const content = <>
    <div className='assignment-thumbnail'>
      {attributes?.thumbnailURL ?
        <img src={attributes.thumbnailURL} alt='' aria-hidden='true' /> :
        <span className='thumbnail-fallback' aria-hidden='true'>{(attributes?.name ?? '?').slice(0, 1)}</span>}
    </div>

    <div className='assignment-body'>
      <h3>{attributes?.name ?? t('unavailableModule')}</h3>

      {attributes?.description ?
        <p className='assignment-description'>{attributes.description}</p> :
        module ? null : <p className='assignment-description'>{t('moduleUnavailable')}</p>}

      <div className='assignment-tags'>
        {isCompleted ? <span className='assignment-tag completed'>
          {t('completed')}
        </span> : <span className={completedCount > 0 ? 'assignment-tag in-progress' : 'assignment-tag'}>
          {completedCount > 0 ?
            t('lessonsComplete', { count: completedCount }) :
            t('notStarted')}
        </span>}

        {!isCompleted && dueDate ? <span className={isOverdue ? 'assignment-tag overdue' : 'assignment-tag due'}>
          {t(isOverdue ? 'overdue' : 'due', { date: formatDate(dueDate) })}
        </span> : null}

        {props.otherTeamName ? <span className='assignment-tag muted'>
          {t('via', { team: props.otherTeamName })}
        </span> : null}
      </div>
    </div>
  </>;

  return <li><div className='assignment-row is-static'>{content}</div></li>
}

// Dates arrive as ISO 8601. Anything unparseable is treated as absent rather
// than rendered as "Invalid Date".
function parseDate(value?: string | null) {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(date: Date) {
  return date.toLocaleDateString(i18next.language, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric'
  });
}

// Soonest due first, since that's what a staff member is scanning for. Anything
// without a due date sorts after, by module name.
function sortAssignments(assignments: TrainingVolunteerAssignmentBundle[]) {
  return [...assignments].sort((one, two) => {
    const dueOne = parseDate(one.assignment.dueDate)?.getTime() ?? null;
    const dueTwo = parseDate(two.assignment.dueDate)?.getTime() ?? null;

    if (dueOne !== dueTwo) {
      if (dueOne === null) return 1;
      if (dueTwo === null) return -1;

      return dueOne - dueTwo;
    }

    const nameOne = one.module?.attributes.name ?? '';
    const nameTwo = two.module?.attributes.name ?? '';

    return nameOne.localeCompare(nameTwo);
  });
}
